import React from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  Stack,
  Typography,
  alpha,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  AutoAwesomeOutlined,
  Close as CloseIcon,
  MovieFilterOutlined,
  PhotoOutlined,
  TokenOutlined,
} from "@mui/icons-material";
import IconButton from "@mui/material/IconButton";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { UserContext } from "../../context/UserContext";

const WELCOME_QUERY = "welcome";
const STORAGE_PREFIX = "beyundai_welcome_credits_shown_";

export function welcomeCreditsStorageKey(uid) {
  return `${STORAGE_PREFIX}${uid || "anon"}`;
}

/**
 * Big welcome modal for newly registered users after sign-in.
 * Triggered by `?welcome=1` (cleared after dismiss) once per account.
 */
export default function WelcomeCreditsModal({ lang }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const history = useHistory();
  const { userData } = React.useContext(UserContext);
  const [open, setOpen] = React.useState(false);

  const credits = Number(userData?.credits ?? 0);
  const uid = userData?.uid || userData?.id || "";
  const creditLabel = Number.isFinite(credits) && credits > 0 ? credits : null;

  React.useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const welcome = params.get(WELCOME_QUERY) === "1";
    if (!welcome) return;
    if (uid && localStorage.getItem(welcomeCreditsStorageKey(uid)) === "1") {
      clearWelcomeQuery(history);
      return;
    }
    setOpen(true);
  }, [uid, history]);

  const dismiss = React.useCallback(() => {
    setOpen(false);
    if (uid) localStorage.setItem(welcomeCreditsStorageKey(uid), "1");
    clearWelcomeQuery(history);
  }, [uid, history]);

  const goTo = (page) => {
    dismiss();
    history.push(`/user?page=${page}`);
  };

  const isDark = theme.palette.mode === "dark";

  return (
    <Dialog
      open={open}
      onClose={dismiss}
      fullScreen={isMobile}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        elevation: 0,
        sx: {
          borderRadius: isMobile ? 0 : 3,
          overflow: "hidden",
          background: isDark
            ? `linear-gradient(160deg, ${alpha(theme.palette.background.paper, 1)} 0%, ${alpha(
                theme.palette.primary.main,
                0.12,
              )} 100%)`
            : theme.palette.background.paper,
          border: `1px solid ${alpha(theme.palette.divider, isDark ? 0.6 : 1)}`,
        },
      }}
      slotProps={{
        backdrop: {
          sx: {
            backdropFilter: "blur(10px)",
            backgroundColor: alpha("#000", isDark ? 0.72 : 0.45),
          },
        },
      }}
    >
      <Box sx={{ position: "absolute", top: 8, right: 8, zIndex: 1 }}>
        <IconButton onClick={dismiss} aria-label="Close" size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <DialogContent sx={{ px: { xs: 3, sm: 4 }, py: { xs: 4, sm: 5 }, textAlign: "center" }}>
        <Box
          sx={{
            width: 72,
            height: 72,
            mx: "auto",
            mb: 2.5,
            borderRadius: "50%",
            display: "grid",
            placeItems: "center",
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.25)}, ${alpha(
              theme.palette.warning?.main || "#fb923c",
              0.2,
            )})`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.35)}`,
          }}
        >
          <AutoAwesomeOutlined color="primary" sx={{ fontSize: 34 }} />
        </Box>

        <Typography variant="h4" fontWeight={900} sx={{ letterSpacing: -0.5, mb: 1 }}>
          {lang?.welcomeCreditsTitle || "Welcome to Beyund AI"}
        </Typography>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 420, mx: "auto" }}>
          {lang?.welcomeCreditsBody ||
            "Your account is ready. We’ve added free credits so you can start creating right away."}
        </Typography>

        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 1.25,
            px: 2.5,
            py: 1.5,
            mb: 3,
            borderRadius: 999,
            bgcolor: alpha(theme.palette.primary.main, isDark ? 0.18 : 0.1),
            border: `1px solid ${alpha(theme.palette.primary.main, 0.35)}`,
          }}
        >
          <TokenOutlined color="primary" />
          <Typography variant="h5" fontWeight={800} color="primary.main">
            {creditLabel != null
              ? `${creditLabel.toLocaleString()} ${lang?.credits || "credits"}`
              : lang?.welcomeCreditsReady || "Your free credits are ready"}
          </Typography>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3.5, maxWidth: 440, mx: "auto" }}>
          {lang?.welcomeCreditsHint ||
            "Use them in Image Studio or Video Studio to generate your first work. You can always return to Billing to top up your credit wallet."}
        </Typography>

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          justifyContent="center"
          sx={{ mb: 1.5 }}
        >
          <Button
            variant="contained"
            size="large"
            startIcon={<PhotoOutlined />}
            onClick={() => goTo("influencers")}
            sx={{ fontWeight: 800, textTransform: "none", px: 2.5 }}
          >
            {lang?.welcomeCreditsImageCta || "Start in Image Studio"}
          </Button>
          <Button
            variant="outlined"
            size="large"
            startIcon={<MovieFilterOutlined />}
            onClick={() => goTo("video-studio")}
            sx={{ fontWeight: 800, textTransform: "none", px: 2.5 }}
          >
            {lang?.welcomeCreditsVideoCta || "Open Video Studio"}
          </Button>
        </Stack>

        <Button
          onClick={dismiss}
          color="inherit"
          sx={{ mt: 0.5, textTransform: "none", color: "text.secondary" }}
        >
          {lang?.welcomeCreditsStayCta || "Maybe later — explore ideas"}
        </Button>
      </DialogContent>
    </Dialog>
  );
}

function clearWelcomeQuery(history) {
  if (typeof window === "undefined") return;
  const params = new URLSearchParams(window.location.search);
  if (!params.has(WELCOME_QUERY)) return;
  params.delete(WELCOME_QUERY);
  const qs = params.toString();
  const next = qs ? `${window.location.pathname}?${qs}` : window.location.pathname;
  if (history?.replace) history.replace(next);
  else window.history.replaceState({}, "", next);
}
